import React, { createContext, useState, useContext, useEffect } from 'react';
import {
    signInWithEmailAndPassword,
    signOut,
    onAuthStateChanged
} from 'firebase/auth';
import { FirebaseAuth } from '../firebase/auth';
import { LoginUser } from '../backend';

const AuthContext = createContext();

export function useAuth() {
    return useContext(AuthContext);
}

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [userDetails, setUserDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    async function login(email, password) {
        try {
            setError(null);
            setLoading(true);

            // First authenticate with Firebase
            const userCredential = await signInWithEmailAndPassword(FirebaseAuth, email, password);

            // Then get user details from backend
            const response = await LoginUser({
                email: userCredential.user.email,
                uid: userCredential.user.uid
            });

            setUserDetails(response?.data?.user);
            return response;
        } catch (error) {
            setError(error.message);
            throw error;
        } finally {
            setLoading(false);
        }
    }

    function logout() {
        setError(null);
        setUserDetails(null);
        return signOut(FirebaseAuth);
    }

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(FirebaseAuth, async (currentUser) => {
            try {
                setError(null);

                if (currentUser) {
                    setUser(currentUser);

                    // Get user details from backend
                    const response = await LoginUser({
                        email: currentUser.email,
                        uid: currentUser.uid
                    });
                    setUserDetails(response?.data?.user);
                } else {
                    setUser(null);
                    setUserDetails(null);
                }
            } catch (error) {
                console.error('Error fetching user details:', error);
                setError('Failed to load user details');
                // Still set the user if Firebase auth succeeded, but userDetails will be null
                if (currentUser) {
                    setUser(currentUser);
                }
            } finally {
                setLoading(false);
            }
        });

        return unsubscribe;
    }, []);

    const value = {
        user,
        userDetails,
        loading,
        error,
        login,
        logout
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}